import * as React from 'react'

import { inject, observer } from 'mobx-react'

import { LocationType, PermitFieldType } from '~/client/graph/types.generated'
import IFormFilterFieldOption from '~/client/src/desktop/interfaces/IFormFilterFieldOption'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'

import FieldSectionHeader from '../../../../../FieldSectionHeader/FieldSectionHeader'
import NewRuleButton from '../../../../../RuleComponents/NewRuleButton'
import DynamicFiltersStore from '../../DynamicFilters.store'
import FilterRule from './components/FilterRule'
import DynamicFiltersListStore from './DynamicFiltersList.store'

interface IProps {
  fieldId: string
  filterFieldsOptions: IFormFilterFieldOption[]
  restrictedLocationIds: string[]
  restrictedLocationTypes: LocationType[]
  filtersStore: DynamicFiltersStore
  materialResourceRecords?: IResourceRecord[]
  isConfiguredFieldMaterialResource?: boolean
  configuredFieldType?: PermitFieldType
  disabledResourceIds?: Set<string>
  renderNewRuleButton?: (onClick: () => void) => JSX.Element

  locationAttributesStore?: LocationAttributesStore
  tagsStore?: TagsStore
}

const INACTIVE_KEY_PREFIX = 'inactive-'

@inject('locationAttributesStore', 'tagsStore')
@observer
export default class DynamicFiltersList extends React.Component<IProps> {
  private store: DynamicFiltersListStore

  public constructor(props: IProps) {
    super(props)

    this.store = new DynamicFiltersListStore(
      props.fieldId,
      props.filterFieldsOptions,
      props.filtersStore,
      props.locationAttributesStore,
      props.tagsStore,
    )
    this.store.updateMaterialResourceRecords(
      props.materialResourceRecords || [],
    )
    this.store.updateIsConfiguredFieldMaterialResource(
      props.isConfiguredFieldMaterialResource || false,
    )
    this.store.updateDisabledResourceIds(props.disabledResourceIds)
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps.fieldId !== this.props.fieldId) {
      this.store.updateFieldId(this.props.fieldId)
    }
    if (
      prevProps.filterFieldsOptions !== this.props.filterFieldsOptions ||
      prevProps.filterFieldsOptions.length !==
        this.props.filterFieldsOptions.length
    ) {
      this.store.updateAvailableFields(this.props.filterFieldsOptions)
    }
    if (prevProps.filtersStore !== this.props.filtersStore) {
      this.store.updateFiltersStore(this.props.filtersStore)
    }
    if (
      prevProps.materialResourceRecords !== this.props.materialResourceRecords
    ) {
      this.store.updateMaterialResourceRecords(
        this.props.materialResourceRecords || [],
      )
    }
    if (
      prevProps.isConfiguredFieldMaterialResource !==
      this.props.isConfiguredFieldMaterialResource
    ) {
      this.store.updateIsConfiguredFieldMaterialResource(
        this.props.isConfiguredFieldMaterialResource || false,
      )
    }
    if (prevProps.disabledResourceIds !== this.props.disabledResourceIds) {
      this.store.updateDisabledResourceIds(this.props.disabledResourceIds)
    }
  }

  public render() {
    const { filtersStore, restrictedLocationIds, restrictedLocationTypes } =
      this.props
    const { filters, isCreationMode, isFormValid, formData, selectedField } =
      filtersStore

    const {
      inactiveFilterIdxs,
      activeFilterIdxs,
      isInactiveExpanded,
      toggleInactiveExpanded,
    } = this.store

    return (
      <>
        {activeFilterIdxs.map(idx => this.renderFilterRule(idx))}

        {!isCreationMode &&
          !!filters.length &&
          (this.props.renderNewRuleButton ? (
            this.props.renderNewRuleButton(filtersStore.enableCreationMode)
          ) : (
            <div className="no-flex relative">
              <NewRuleButton onClick={filtersStore.enableCreationMode} />
            </div>
          ))}

        {isCreationMode && (
          <FilterRule
            isEditMode
            isExpanded
            listStore={this.store}
            filter={formData}
            selectedField={selectedField}
            isFormValid={isFormValid}
            isConfiguredFieldMaterialResource={
              this.props.isConfiguredFieldMaterialResource
            }
            configuredFieldType={this.props.configuredFieldType}
            restrictedLocationIds={restrictedLocationIds}
            restrictedLocationTypes={restrictedLocationTypes}
            onEditClick={filtersStore.createNewFilter}
            onFieldChange={filtersStore.updateFieldId}
            onFieldValuesChange={filtersStore.updateFieldValues}
            onValuesChange={filtersStore.updateValues}
            onRemoveValueFromFilter={filtersStore.removeValueFromFilter}
            onRemove={filtersStore.resetData}
          />
        )}

        {!!inactiveFilterIdxs.length && (
          <FieldSectionHeader
            text={Localization.translator.inactive}
            isExpanded={isInactiveExpanded}
            isIconHidden={!inactiveFilterIdxs.length}
            onToggle={toggleInactiveExpanded}
            className="my10 pt4 w-fit-content"
          />
        )}

        {isInactiveExpanded && (
          <div className="opacity7">
            {inactiveFilterIdxs.map(idx => this.renderFilterRule(idx, true))}
          </div>
        )}
      </>
    )
  }

  private renderFilterRule(idx: number, isInactive?: boolean): React.ReactNode {
    const { filtersStore, restrictedLocationIds, restrictedLocationTypes } =
      this.props
    const { filters, isFormValid, formData, selectedField, filterFieldsMap } =
      filtersStore

    const filter = filters[idx]
    const isEditing = filtersStore.editingFilterIdx === idx
    const selectedFieldForRule = isEditing
      ? selectedField
      : filterFieldsMap.get(filter.fieldId)

    return (
      <FilterRule
        key={isInactive ? `${INACTIVE_KEY_PREFIX}-${idx}` : idx}
        listStore={this.store}
        isExpanded={filtersStore.expandedFilterIdx === idx}
        isEditMode={isEditing}
        isFormValid={isEditing ? isFormValid : undefined}
        isInactive={isInactive}
        filter={isEditing ? formData : filter}
        selectedField={selectedFieldForRule}
        isConfiguredFieldMaterialResource={
          this.props.isConfiguredFieldMaterialResource
        }
        configuredFieldType={this.props.configuredFieldType}
        restrictedLocationIds={restrictedLocationIds}
        restrictedLocationTypes={restrictedLocationTypes}
        onToggle={() => filtersStore.expandFilter(idx)}
        onEditClick={() => filtersStore.editOrSaveFilter(idx)}
        onRemove={() => filtersStore.cancelOrRemoveFilter(idx)}
        onFieldChange={filtersStore.updateFieldId}
        onFieldValuesChange={filtersStore.updateFieldValues}
        onValuesChange={filtersStore.updateValues}
        onRemoveValueFromFilter={filtersStore.removeValueFromFilter}
      />
    )
  }
}
